import { createOutlineScrollTracker } from './outlineScrollTracker';

const LARGE_BREAKPOINT_PX = 1280;
const isDesktop = (): boolean => window.innerWidth >= LARGE_BREAKPOINT_PX;
// Hide sidebar outline items on desktop
const DESKTOP_HIDDEN_OUTLINE_CLASS = 'ungated-outline-hidden-on-desktop';
const OUTLINE_EMPTY_CLASS = 'ungated-outline-empty';

export const setUpFloatingBadges = (): void => {
  const container = document.querySelector(
    '.js-floating-badges',
  ) as HTMLElement;
  if (!container) return;

  const desktopHiddenIdsJson = container.getAttribute(
    'data-desktop-hidden-outline-ids',
  );
  const desktopHiddenIds: string[] = desktopHiddenIdsJson
    ? (JSON.parse(desktopHiddenIdsJson) as string[])
    : [];

  // Hide initially, show when scrolling to the document
  container.classList.add('hidden');

  const actionButtons = document.querySelector(
    '.ungated-work-card--action-buttons',
  ) as HTMLElement;
  const embeddedDocument = document.querySelector(
    '.js-loswp-embedded-document',
  ) as HTMLElement;

  let actionButtonsVisible = true;
  let documentBottomInView = false;

  const updateBadgesVisibility = (): void => {
    const actionButtonsBlockBadges = !isDesktop() && actionButtonsVisible;
    const shouldHide = actionButtonsBlockBadges || documentBottomInView;
    container.classList.toggle('hidden', shouldHide);
  };

  if (actionButtons) {
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        actionButtonsVisible = entries[0].isIntersecting;
        updateBadgesVisibility();
      },
      { threshold: 0 },
    );
    visibilityObserver.observe(actionButtons);
  } else {
    actionButtonsVisible = false;
    updateBadgesVisibility();
  }

  if (embeddedDocument) {
    const checkDocumentBottomInView = (): void => {
      const rect = embeddedDocument.getBoundingClientRect();
      // Bottom is in view when rect.bottom is less than or equal to viewport height
      documentBottomInView = rect.bottom <= window.innerHeight;
      updateBadgesVisibility();
    };

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkDocumentBottomInView();
          ticking = false;
        });
        ticking = true;
      }
    });

    checkDocumentBottomInView();
  }

  // Page number tracking - see build_vu/Attachment.tsx
  const currentPageElement = container.querySelector(
    '.js-current-page',
  ) as HTMLElement;

  if (currentPageElement) {
    const handlePageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ pageNumber: number }>;
      currentPageElement.textContent = String(customEvent.detail.pageNumber);
    };

    window.addEventListener('buildVuPageChanged', handlePageChange);
  }

  const toggle = container.querySelector(
    '.js-floating-outline-toggle',
  ) as HTMLButtonElement;
  const panel = container.querySelector(
    '.js-floating-outline-panel',
  ) as HTMLElement;
  const closeBtn = container.querySelector(
    '.js-floating-outline-close',
  ) as HTMLButtonElement;
  const items = container.querySelectorAll('.js-floating-outline-item');
  const elements = container.querySelectorAll(
    '[class*="js-floating-outline-element-"]',
  ) as NodeListOf<HTMLElement>;
  const progressLine = container.querySelector(
    '.js-floating-progress-line',
  ) as HTMLElement;

  // Outline toggle
  let hasOpenedOnce = false;
  const openPanel = () => {
    panel?.classList.remove('hidden');
    toggle?.setAttribute('aria-expanded', 'true');

    // Recalculate positions when panel first opens
    // Panel starts hidden, so dot offsets are all 0 initially
    // After panel is visible, elements have layout and we can measure
    if (!hasOpenedOnce) {
      hasOpenedOnce = true;
      // Use requestAnimationFrame to ensure layout has happened
      requestAnimationFrame(() => {
        outlineTracker?.calculatePositions();
        outlineTracker?.updateProgressLinePadding();
        outlineTracker?.updateProgress();
      });
    }
  };

  const closePanel = () => {
    panel?.classList.add('hidden');
    toggle?.setAttribute('aria-expanded', 'false');
  };

  let outlineTracker: ReturnType<typeof createOutlineScrollTracker> | null =
    null;
  const updateDesktopHiddenOutlineVisibility = (): void => {
    const desktop = isDesktop();
    elements.forEach((el) => {
      const id = el.getAttribute('data-target-id');
      const hide = desktop && !!id && desktopHiddenIds.includes(id);
      el.classList.toggle(DESKTOP_HIDDEN_OUTLINE_CLASS, hide);
    });
    outlineTracker?.calculatePositions();
    outlineTracker?.updateProgress();

    // Hide Outline button when all items are hidden on desktop
    if (desktopHiddenIds.length) {
      const visibleCount = Array.from(elements).filter(
        (el) => !el.classList.contains(DESKTOP_HIDDEN_OUTLINE_CLASS),
      ).length;
      if (visibleCount === 0) {
        container.classList.add(OUTLINE_EMPTY_CLASS);
        if (panel && !panel.classList.contains('hidden')) {
          closePanel();
        }
      } else {
        container.classList.remove(OUTLINE_EMPTY_CLASS);
      }
    }
  };

  // Apply desktop hidden classes BEFORE creating the tracker
  // This ensures calculatePositions() on initial setup uses the correct visibility
  if (desktopHiddenIds.length) {
    const desktop = isDesktop();
    elements.forEach((el) => {
      const id = el.getAttribute('data-target-id');
      const hide = desktop && !!id && desktopHiddenIds.includes(id);
      if (hide) {
        el.classList.add(DESKTOP_HIDDEN_OUTLINE_CLASS);
      }
    });
  }

  outlineTracker = createOutlineScrollTracker({
    elements,
    progressLine,
    dotSelector: '.floating-badges__dot',
    progressCssVar: '--progress',
    isElementHidden: desktopHiddenIds.length
      ? (el) => el.classList.contains(DESKTOP_HIDDEN_OUTLINE_CLASS)
      : undefined,
  });

  if (desktopHiddenIds.length) {
    updateDesktopHiddenOutlineVisibility();
    window
      .matchMedia(`(min-width: ${LARGE_BREAKPOINT_PX}px)`)
      .addEventListener('change', updateDesktopHiddenOutlineVisibility);
  }

  toggle?.addEventListener('click', () => {
    toggle.getAttribute('aria-expanded') === 'true'
      ? closePanel()
      : openPanel();
  });

  closeBtn?.addEventListener('click', closePanel);

  // On single-panel (small) screens, outline links to work-card sections (title, abstract,
  // etc.) need to expand the work card first, then scroll to the section.
  const expandWorkCardThenScrollToSection = (targetId: string): void => {
    const toggleButton = document.querySelector(
      '.js-info-badge-toggle',
    ) as HTMLButtonElement | null;
    const collapsibles = document.querySelectorAll<HTMLElement>(
      '.js-info-badge-collapsible',
    );
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'true');
    }
    collapsibles.forEach((el) => {
      el.setAttribute('aria-hidden', 'false');
      el.classList.add('expanded');
    });
    const targetEl = document.getElementById(targetId);
    if (targetEl) {
      targetEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  items.forEach((item) => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const link = item as HTMLAnchorElement;
      const href = link.getAttribute('href') ?? '';
      const targetId = href.startsWith('#') ? href.slice(1) : '';

      if (!targetId) return;

      // For hidden sections on mobile, expand work card first
      if (desktopHiddenIds.includes(targetId) && !isDesktop()) {
        expandWorkCardThenScrollToSection(targetId);
      } else {
        // For all other sections, smooth scroll directly
        const targetEl = document.getElementById(targetId);
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel && !panel.classList.contains('hidden')) {
      closePanel();
    }
  });

  document.addEventListener('click', (e) => {
    if (!panel || panel.classList.contains('hidden')) return;
    const target = e.target as Node;
    if (!panel.contains(target) && !toggle?.contains(target)) {
      closePanel();
    }
  });

  // Adjust for sticky footer
  const footer = document.querySelector('.js-sticky-cta-footer') as HTMLElement;
  if (footer) {
    const obs = new MutationObserver(() => {
      container.classList.toggle(
        'footer-visible',
        !footer.classList.contains('hidden'),
      );
    });
    obs.observe(footer, { attributes: true });
    container.classList.toggle(
      'footer-visible',
      !footer.classList.contains('hidden'),
    );
  }
};
